import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from 'prop-types';
import { withRouter } from "react-router-dom";
import { Row, Col, Form, Input, Button, Select, Checkbox, Spin } from 'antd';
import IntlMessages from "../../../components/utility/intlMessages";
import { DvIntlMessages } from "../../../components/utility/dvGeneralUtilities";
import systemActions from '../../../redux/systemInfos/actions';
import styled from "styled-components";


const Container = styled.div`
.ant-form-item label {
  width: 180px;
}
.full-width .ant-checkbox-wrapper {
  width: 100%;
}
.ant-checkbox-wrapper {
  width: 100%;
}
`;

const { createNewUser, getAvailablePermissions } = systemActions;

function CreateUserForm({
  availablePermissions,
  match,
  getAvailablePermissions,
  createNewUser,
  closeModal
}) {
  const [form] = Form.useForm();
  const [formStage, setFormStage] = useState(0);

  const getDefaultPermissions = () => {
    return availablePermissions.items
      ?.filter(item => ![
        'delete-patient-of-any-user',
        'delete-hrc-of-any-user',
        'delete-visit-of-any-user',
      ]
      .includes(item)) ?? [];
  };

  useEffect(() => {
    if (!availablePermissions.isLoaded) getAvailablePermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (availablePermissions.isLoaded && !availablePermissions.isLoading) {
      setPermissions(getDefaultPermissions());
    }
  }, [availablePermissions]);
  
  const [permissions, setPermissions] = useState(getDefaultPermissions());

  const onFinish = (values) => {
    const bodyToSend = {
      ...values,
      clinicId: parseInt(match.params?.clinicId),
      permissions: permissions ?? [],
    }
    delete bodyToSend.confirm;
    createNewUser(bodyToSend, match.params?.clinicId);
    closeModal();
  };

  const emailValidator = (rule, value) => {
    const emailRegEx = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(.\w{2,3})+$/;
    if (rule['required'] && !value) {
      return Promise.reject(DvIntlMessages('registration.requiredEmail'));
    }
    if (value && value.length !== 0 && !emailRegEx.test(String(value).toLowerCase())) {
      return Promise.reject(DvIntlMessages('registration.invalidEmailType'));
    }
    return Promise.resolve();
  };

  const phoneNumberValidator = (rule, value, callback) => {
    const phoneNumberRegEx = /^\+?([ ]?[-.(]?[\d]+[-.)]?)+$/;
    if (value && value.length !== 0 && !phoneNumberRegEx.test(String(value))) {
      callback(<IntlMessages id="error.phoneNumberValidationError" />);
    } else {
      callback();
    }
  };

  const firstStageValidation = async () => {
    try {
      await form.validateFields([
        'firstName',
        'lastName',
        'password',
        'confirm',
      ]);
      setFormStage(1);
    } catch (errorInfo) {
      console.log('Validation failed:', errorInfo);
    }
  };

  return (
    <Container>
      <Row style={{ marginTop: "25px" }}>
        <Col span={24}>
          <Form form={form} name="createNewClinic" onFinish={onFinish}>
            {(formStage === 0 || formStage === 1) && <>
              <Form.Item
                name="firstName"
                label={<IntlMessages id="general.firstName" />}
                rules={[{ required: true }]}
              >
                <Input placeholder={DvIntlMessages("general.firstName")} autoComplete="firstname" />
              </Form.Item>

              <Form.Item
                name="lastName"
                label={<IntlMessages id="general.lastName" />}
                rules={[{ required: true }]}
              >
                <Input placeholder={DvIntlMessages("general.lastName")} autoComplete="lastname" />
              </Form.Item>

              {/* In order to fool the stupid auto-complete of brwosers... */}
              <div style={{position: 'absolute', top: -1000}}>
                <input type="text" name="username" />
              </div>

              <Form.Item
                name="password"
                label={<IntlMessages id="signin.password" />}
                rules={[
                  {
                    required: true,
                    message: DvIntlMessages('registration.passwordRequired'),
                  },
                  {
                    min: 6,
                    message: DvIntlMessages("registration.passwordMinimum"),
                  }
                ]}
                hasFeedback
              >
                <Input.Password id="real-password" autoComplete="new-password" placeholder={DvIntlMessages("signin.password")} />
              </Form.Item>

              <Form.Item
                name="confirm"
                label={<IntlMessages id="registration.passwordConfirm" />}
                dependencies={['password']}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: DvIntlMessages('registration.passwordConfirmRequired')
                  },
                  ({ getFieldValue }) => ({
                    validator(rule, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(DvIntlMessages('registration.passwordConfirmIncorrect'));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder={DvIntlMessages("registration.passwordConfirm")} autoComplete="confirm-password" />
              </Form.Item>

              {formStage === 0 && <Row justify='end' gutter={[16, 16]}>
                <Col>
                  <Button onClick={() => firstStageValidation()}>
                    <IntlMessages id="general.next" />
                  </Button>
                </Col>
              </Row>}
            </>}

            {formStage === 1 && <>
              <Form.Item
                name="email"
                label={<IntlMessages id="general.email" />}
                rules={[
                  {
                    required: true,
                    validator: emailValidator
                  },
                ]}
              >
                <Input placeholder={DvIntlMessages("general.email")} />
              </Form.Item>

              <Form.Item
                name="phone"
                label={<IntlMessages id="general.phone" />}
                rules={[
                  {
                    required: false,
                    validator: phoneNumberValidator
                  },
                  {
                    message: DvIntlMessages("input.noLongerThan20"),
                    max: 20
                  }
                ]}
              >
                <Input placeholder={DvIntlMessages("general.phone")} />
              </Form.Item>

              <Form.Item
                name="specialty"
                label={<IntlMessages id="general.specialty" />}
              >
                <Input placeholder={DvIntlMessages("general.specialty")} />
              </Form.Item>

              <Form.Item
                name="role"
                label={<IntlMessages id="general.role" />}
                rules={[{
                  required: true,
                  message: DvIntlMessages('createUserForm.role.required')
                }]}
              >
                <Select>
                  <Select.Option value="doctor"> <IntlMessages id="general.doctor" /> </Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="Permissions">
                <Spin spinning={availablePermissions.isLoading}>
                  <Checkbox.Group
                    value={permissions}
                    className="full-width"
                    onChange={(checkedValues => setPermissions(checkedValues))}
                    options={availablePermissions.items.map(p => {
                      return { label: p, value: p };
                    })} />
                </Spin>
              </Form.Item>

              <Row justify='end' gutter={[16, 16]}>
                <Col>
                  <Button onClick={closeModal}>
                    <IntlMessages id="general.cancel" />
                  </Button>
                </Col>
                <Col>
                  <Button type="primary" htmlType="submit">
                    <IntlMessages id="general.submit" />
                  </Button>
                </Col>
              </Row>
            </>}

          </Form>
        </Col>
      </Row>
    </Container>
  )
}

CreateUserForm.propTypes = {
  closeModal: PropTypes.func.isRequired,
  getAvailablePermissions: PropTypes.func.isRequired,
  createNewUser: PropTypes.func.isRequired
};

const mapActionsToProps = {
  createNewUser,
  getAvailablePermissions
};

function mapStateToProps(state) {
  return {
    availablePermissions: state.SystemInfos.get('availablePermission'),
  };
}

const wrappedCreateUserForm = withRouter(CreateUserForm);
const connectedCreateUserForm = connect(mapStateToProps, mapActionsToProps)(wrappedCreateUserForm);
export default connectedCreateUserForm;
